/**
 * metadataParser.js
 * Converts backend metadata JSON into AG Grid columnDefs and HyperFormula config.
 * This is the single point of truth for metadata → rendering transformation.
 */

import { rendererRegistry } from '../../grid/renderers/rendererRegistry';
import { editorRegistry }   from '../../grid/editors/editorRegistry';
import { columnIndexToLetter } from '../../utils/columnLetterUtils';

// ─── Types ────────────────────────────────────────────────────────────────────
// Supported datatypes: string | number | currency | percent | date | formula | badge | boolean

// ─── Main Parser ─────────────────────────────────────────────────────────────

/**
 * Parse a sheet's metadata columns into AG Grid columnDefs.
 * @param {Object} sheetMeta  - { sheetName, permissions, columns, ... }
 * @param {Object} permissions - { canEdit: bool, ... }
 * @returns {Array} AG Grid columnDef array
 */
export function parseColumnsToAgDefs(sheetMeta, permissions = {}) {
  const { columns = [] } = sheetMeta;

  return columns.map((col, idx) => {
    const canEdit = permissions.canEdit !== false && col.editable !== false && !col.formula;

    const def = {
      field:            col.field,
      headerName:       col.headerName || col.field,
      width:            col.width     || getDefaultWidth(col.datatype),
      minWidth:         col.minWidth  || 80,
      maxWidth:         col.maxWidth  || undefined,
      pinned:           col.pinned    || null,
      editable:         canEdit,
      sortable:         true,
      filter:           getFilterType(col.datatype),
      resizable:        true,
      suppressMenu:     false,
      hide:             col.hidden    || false,

      cellRenderer:     resolveCellRenderer(col),
      cellRendererParams: { colMeta: col },
      cellEditor:       resolveCellEditor(col),
      cellEditorParams: resolveCellEditorParams(col),
      valueFormatter:   resolveValueFormatter(col),
      cellStyle:        buildCellStyleFn(col, permissions),
      cellClassRules:   buildCellClassRules(col),
      tooltipField:     col.tooltipField || undefined,
      tooltipValueGetter: col.validation ? validationTooltipGetter(col) : undefined,
      headerTooltip:    col.description || col.headerName || col.field,
      type:             resolveColType(col.datatype),
    };

    return def;
  });
}

/**
 * Extract formula metadata from columns for HyperFormula seeding.
 * Returns [{ colIndex, formula }] for columns that have default formulas.
 */
export function extractFormulaColumns(columns) {
  return columns
    .map((col, idx) => ({ colIndex: idx, col }))
    .filter(({ col }) => col.formula)
    .map(({ colIndex, col }) => ({ colIndex, formula: col.formula, field: col.field }));
}

/**
 * Build a HyperFormula-ready row array from a data row + column metadata.
 * Formula fields get their formula string; data fields get their value.
 */
export function buildHfRow(dataRow, columns) {
  return columns.map((col) => {
    const rawVal = dataRow[col.field];
    if (col.formula) return col.formula;
    if (typeof rawVal === 'string' && rawVal.startsWith('=')) return rawVal;
    return rawVal ?? null;
  });
}

/**
 * Build the full HyperFormula sheets object from metadata + data map.
 * { SheetName: [[row0col0, row0col1, ...], [row1col0, ...]] }
 */
export function buildHfSheetsData(sheetsMeta, dataMap) {
  const result = {};

  sheetsMeta.forEach(({ sheetId, sheetName, columns }) => {
    const rows = dataMap[sheetId] || [];
    result[sheetName] = rows.map((row) => buildHfRow(row, columns));
  });

  return result;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveCellRenderer(col) {
  if (col.datatype === 'badge')    return rendererRegistry.badge;
  if (col.datatype === 'boolean')  return rendererRegistry.boolean;
  if (col.datatype === 'progress') return rendererRegistry.progress;
  return undefined;
}

function resolveCellEditor(col) {
  if (!col.editable) return undefined;
  return editorRegistry[col.datatype] || editorRegistry.default;
}

function resolveCellEditorParams(col) {
  const base = { colMeta: col };
  if (col.datatype === 'date')   return { ...base, dateFormat: col.dateFormat || 'DD-MM-YYYY' };
  if (col.options)               return { ...base, values: col.options };
  return base;
}

function resolveValueFormatter(col) {
  switch (col.datatype) {
    case 'currency':
      return (p) => {
        if (p.value == null || p.value === '') return '—';
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: col.currency || 'INR', maximumFractionDigits: col.decimals ?? 0 }).format(p.value);
      };
    case 'percent':
      return (p) => p.value == null ? '—' : `${Number(p.value).toFixed(col.decimals ?? 1)}%`;
    case 'number':
      return (p) => p.value == null ? '—' : Number(p.value).toLocaleString('en-IN', { maximumFractionDigits: col.decimals ?? 2 });
    case 'date':
      return (p) => {
        if (!p.value) return '—';
        try { return new Date(p.value).toLocaleDateString('en-IN'); } catch { return p.value; }
      };
    default:
      return undefined;
  }
}

function getFilterType(datatype) {
  if (['number', 'currency', 'percent'].includes(datatype)) return 'agNumberColumnFilter';
  if (datatype === 'date') return 'agDateColumnFilter';
  return 'agTextColumnFilter';
}

function getDefaultWidth(datatype) {
  const widths = { string: 180, currency: 140, percent: 110, number: 130, date: 130, badge: 130, boolean: 90 };
  return widths[datatype] || 140;
}

function resolveColType(datatype) {
  if (['number', 'currency', 'percent'].includes(datatype)) return 'numericColumn';
  return undefined;
}

function buildCellStyleFn(col, permissions) {
  const staticStyle = col.cellStyle || {};
  const isLocked    = !col.editable || !permissions.canEdit;

  return (params) => {
    let style = { ...staticStyle };

    if (isLocked) {
      style = { ...style, background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' };
    }

    if (col.formula) {
      style = { ...style, background: '#eef2ff', color: '#3730a3', fontStyle: 'italic' };
    }

    const cf = col.conditionalFormatting;
    if (cf && params.value != null) {
      const v = Number(params.value);
      if (cf.negative && v < 0) style = { ...style, ...cf.negative };
      else if (cf.positive && v > 0) style = { ...style, ...cf.positive };
      else if (cf.zero    && v === 0) style = { ...style, ...cf.zero    };

      if (cf.thresholds) {
        for (const rule of cf.thresholds) {
          if (
            (rule.operator === '>='  && v >= rule.value) ||
            (rule.operator === '>'   && v >  rule.value) ||
            (rule.operator === '<='  && v <= rule.value) ||
            (rule.operator === '<'   && v <  rule.value) ||
            (rule.operator === '==' && v === rule.value)
          ) {
            style = { ...style, background: rule.background, color: rule.color };
            break;
          }
        }
      }
    }

    return style;
  };
}

function buildCellClassRules(col) {
  return {
    'cell-editable': () => col.editable && !col.formula,
    'cell-formula':  () => !!col.formula,
    'cell-locked':   () => !col.editable,
    'cell-negative': (p) => col.datatype === 'currency' && Number(p.value) < 0,
  };
}

function validationTooltipGetter(col) {
  return (params) => {
    const v = col.validation;
    if (!v) return null;
    const val = params.value;
    if (v.required && (val === null || val === undefined || val === '')) return `${col.headerName}: Required`;
    if (v.min !== undefined && Number(val) < v.min) return `Min value: ${v.min}`;
    if (v.max !== undefined && Number(val) > v.max) return `Max value: ${v.max}`;
    return null;
  };
}
